import { IServerPagination } from "app/model"
import { getPercent } from "app/tools/common"
import { getBaseUrl } from "app/tools/repository"
import { CampaignAPI, ForecastAPI, MediaAPI, SspMediaApprovalAPI } from "generated-api"
import { NOT_FOUND_PATH } from "page/model"
import { checkIfSessionIsOrWillExpired, getToken, getUserAgency } from "user/tools/common"
import {
  APPROVAL_STATUSES,
  ICampaignMedia,
  ICampaignMediaCreative,
  ICampaignMediaDTO,
  ICampaignMediaVideo,
  IMedia,
  IMediaApprovalData,
  IMediaCreateParams,
  IMediaCreativeForecast,
  IMediaCreativeForecastById,
  IMediaDTO,
  IMediaRepository,
  IMediaType,
  IMediaVideo,
  IMediaVideoCreatParams,
  ISspMediaApproval,
  ISspMediaApprovalFilters,
  ISspMediaApprovalsByMedia,
  MEDIA_APPROVAL_STATUSES,
  MEDIA_SIMPLE_TYPES
} from "./model"
import { getMediaCreativeForecastIdFromForecast, getMediaType } from "./tools/common"

export const mediaRPO: IMediaRepository = {
  getMediaTypes(): Promise<IMediaType[]> {
    return new Promise<IMediaType[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        MediaAPI.getApiMediaTypeList()
          .then(response => resolve(response))
          .catch((error: any) => {
            console.error("Server error while getting all media types", error)
            reject("media.type.get.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while getting all media types", ex)
        reject("media.type.get.error.unexpected")
      }
    })
  },
  getMediaByName(name: string): Promise<IMediaDTO[]> {
    return new Promise<IMediaDTO[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        MediaAPI.getApiMediaList(
          0,
          undefined,
          name,
          "contains",
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          getUserAgency().id,
          false,
          false
        )
          .then(response => resolve(response))
          .catch((error: any) => {
            console.error("Server error while getting media by name", error)
            reject("media.get.by.name.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while getting media by name", ex)
        reject("media.get.by.name.error.unexpected")
      }
    })
  },
  getMediasById(ids: number[]): Promise<IMediaDTO[]> {
    return new Promise<IMediaDTO[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        MediaAPI.getApiMediaList(
          0,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          ids,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          false
        )
          .then(response => resolve(response))
          .catch((error: any) => {
            console.error("Server error while getting medias by ids", error)
            reject("media.get.by.ids.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while getting medias by ids", ex)
        reject("media.get.by.ids.error.unexpected")
      }
    })
  },
  createMedia(params: IMediaCreateParams): Promise<IMedia> {
    return new Promise<IMedia>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        MediaAPI.postApiMedia(params)
          .then(response => resolve({ ...response, creatives: [], videos: [] }))
          .catch((error: any) => {
            console.error("Server error while creating media", error)
            reject("media.create.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while creating media", ex)
        reject("media.create.error.unexpected")
      }
    })
  },
  getCampaignMedias(campaignId: number): Promise<IMedia[]> {
    return new Promise<IMedia[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.getApiCampaignMediaList(campaignId)
          .then(response =>
            resolve(response.map(campaignMedia => getMediaFromCampaignMedia(campaignMedia as ICampaignMedia)))
          )
          .catch((error: any) => {
            console.error("Server error while getting campaign medias", error)
            reject("media.campaign.get.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while getting campaign medias", ex)
        reject("media.campaign.get.error.unexpected")
      }
    })
  },
  getCampaignMedia(campaignId: number, mediaId: number): Promise<IMedia> {
    return new Promise<IMedia>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.getApiCampaignMedia(campaignId, mediaId)
          .then(response => {
            if (!response) {
              reject(`Media id="${mediaId}" for campaign id="${campaignId}" not found`)
              return
            }
            resolve(getMediaFromCampaignMedia(response as ICampaignMedia))
          })
          .catch((error: any) => {
            console.error("Server error while getting campaign media", error)
            if (error.status === 404) {
              return reject(NOT_FOUND_PATH)
            }
            reject("media.campaign.get.error.server")
          })
      } catch (ex: any) {
        console.error("Unexpected error while getting campaign media", ex)
        reject("media.campaign.get.error.unexpected")
      }
    })
  },
  createCampaignMedia(campaignId: number, mediaId: number): Promise<ICampaignMedia> {
    return new Promise<ICampaignMedia>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.postApiCampaignMedia({
          campaign_id: campaignId,
          media_id: mediaId,
          parent_campaign_media_id: undefined,
          approval_status_id: APPROVAL_STATUSES.PENDING
        })
          .then(response => {
            if (!response) {
              return reject("Unable to create campaign media")
            }
            resolve(response as ICampaignMedia)
          })
          .catch((error: any) => {
            console.error("Server error while creating campaign media", error)
            reject("media.campaign.create.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while creating campaign media", ex)
        reject("media.campaign.create.error.unexpected")
      }
    })
  },
  deleteCampaignMedia(campaignId: number, mediaId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.deleteApiCampaignMedia(campaignId, mediaId)
          .then(() => resolve())
          .catch((error: any) => {
            console.error("Server error while deleting media", error)
            reject("media.delete.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while deleting media", ex)
        reject("media.delete.error.unexpected")
      }
    })
  },
  deleteMediaCreative(mediaId: number, creativeId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        MediaAPI.deleteApiMediaCreative(mediaId, creativeId)
          .then(() => resolve())
          .catch((error: any) => {
            console.error("Server error while deleting creative media", error)
            reject("media.creative.delete.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while deleting creative media", ex)
        reject("media.creative.delete.error.unexpected")
      }
    })
  },
  getMediaCreativeForecast(campaignId: number): Promise<IMediaCreativeForecastById> {
    return new Promise<IMediaCreativeForecastById>((resolve, reject) => {
      /* Ex:
      "result": {
        "total_screens": 198175,
        "total_impressions": 32038014050,
        "total_spend": 301267776.29965526,
        "groups": [
          {
            "screen_count": 92535,
            "impressions": 10781716610,
            "worth_cad": 114557191.95620494,
            "format": {
              "width": 1920,
              "height": 1080
            },
            "is_video": 0
          },
          ...
        ]
      }
      */
      try {
        checkIfSessionIsOrWillExpired()
        ForecastAPI.getApiForecastDoohFormatIsVideo(campaignId)
          .then(response => {
            const forecasts: IMediaCreativeForecastById = {}
            if (response.groups) {
              response.groups.map((g: any) => {
                const forecast: IMediaCreativeForecast = {
                  forecastId: "",
                  mediaType: g.is_video ? MEDIA_SIMPLE_TYPES.VIDEO : MEDIA_SIMPLE_TYPES.IMAGE,
                  width: g.format.width,
                  height: g.format.height,
                  screen: g.screen_count,
                  screenPercent: getPercent(g.screen_count, response.total_screens),
                  screenTotal: response.total_screens,
                  impression: g.impressions,
                  impressionPercent: getPercent(g.impressions, response.total_impressions),
                  impressionTotal: response.total_impressions,
                  spendInCad: g.worth_cad,
                  spendTotalInCad: response.total_spend,
                  formatId: g.format_id
                }
                const forecastId = getMediaCreativeForecastIdFromForecast(forecast)
                forecast.forecastId = forecastId
                forecasts[forecastId] = forecast
              })
            }
            resolve(forecasts)
          })
          .catch((error: any) => {
            console.error("Server error while getting creative media forecast", error)
            reject("media.creative.get.forecast.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while getting creative media forecast", ex)
        reject("media.creative.get.forecast.error.unexpected")
      }
    })
  },
  createMediaVideo(params: IMediaVideoCreatParams): Promise<ICampaignMediaVideo> {
    return new Promise<ICampaignMediaVideo>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        MediaAPI.postApiMediaVideo(params)
          .then(response => resolve(response))
          .catch((error: any) => {
            console.error("Server error while creating media video", error)
            reject("media.video.create.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while creating media video", ex)
        reject("media.video.create.error.unexpected")
      }
    })
  },
  deleteMediaVideo(mediaId: number, videoId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        MediaAPI.deleteApiMediaVideo(mediaId, videoId)
          .then(() => resolve())
          .catch((error: any) => {
            console.error("Server error while deleting media video", error)
            reject("media.video.delete.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while deleting media video", ex)
        reject("media.video.delete.error.unexpected")
      }
    })
  },
  getMediaCreativeUploadUrl(mediaId: number): string {
    return `${getBaseUrl()}/api/media/${mediaId}/creative/upload`
  },
  getMediaCreativeDownloadUrl(mediaId: number, creativeId: number): string {
    return `${getBaseUrl()}/api/media/${mediaId}/creative/${creativeId}/download?adstanding_session_id=${getToken()}`
  },
  getMediaImageDownloadUrl(mediaId: number): string {
    return `${getBaseUrl()}/api/media/${mediaId}/download?adstanding_session_id=${getToken()}`
  },
  getMediaVideoDownloadUrl(mediaId: number, videoId: number): string {
    return `${getBaseUrl()}/api/media/${mediaId}/video/${videoId}/download?adstanding_session_id=${getToken()}`
  },
  getAllMediaApprovals(): Promise<IMediaApprovalData[]> {
    return new Promise<IMediaApprovalData[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        SspMediaApprovalAPI.getApiSspMediaApprovalMediaList(
          undefined, // offset?: number,
          undefined, // limit?: number,
          undefined, // orderby?: Array<string>,
          undefined, // comparator?: Array<string>,
          undefined, // mediaId?: Array<number>,
          undefined, // sspId?: Array<number>,
          undefined, // sspPublisherId?: Array<number>,
          undefined, // approvalStatusId?: Array<'approved' | 'pending' | 'refused'>,
          undefined, // advertiserName?: string,
          undefined, // category?: string,
          undefined, // language?: Array<'de' | 'el' | 'en' | 'es' | 'fr' | 'it' | 'ja' | 'pt' | 'zh'>,
          undefined // Array<string | null>,
        )
          .then(async response =>
            resolve(getMediaApprovalDatas(response.filter(a => a.ssp_approval && a.ssp_approval.length > 0)))
          )
          .catch((error: any) => {
            console.error("Server error while getting media approvals", error)
            reject("media.approval.get.all.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while getting media approvals", ex)
        reject("media.approval.get.all.error.unexpected")
      }
    })
  },
  getMediaApprovals(filters: ISspMediaApprovalFilters, pagination?: IServerPagination): Promise<ISspMediaApproval[]> {
    return new Promise<ISspMediaApproval[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        const sort: string[] = []
        if (!pagination) {
          sort.push("created_at,desc")
        }
        const offset = pagination ? pagination.page * pagination.pageSize : undefined
        const limit = pagination ? pagination.pageSize : undefined
        if (pagination && pagination.orderBy) {
          sort.push(`${pagination.orderBy},${pagination.order || "asc"}`)
        }
        let comparators = undefined
        if (filters.createdAt.date) {
          comparators = ["created_at,gte", "created_at,lte"]
        }
        SspMediaApprovalAPI.getApiSspMediaApprovalList(
          offset, // offset?: number,
          limit, // limit?: number,
          sort.length > 0 ? sort : undefined, // orderby?: Array<string>,
          comparators, // comparator?: Array<string>,
          undefined, // mediaId?: Array<number>,
          undefined, // sspId?: Array<number>,
          undefined, // sspPublisherId?: Array<number>,
          filters.approvalStatuses.length > 0 ? filters.approvalStatuses : undefined, // approvalStatusId?: Array<'approved' | 'pending' | 'refused'>,
          undefined, // advertiserName?: string,
          undefined, // category?: string,
          filters.languages.length > 0 ? filters.languages : undefined, // language?: Array<'de' | 'el' | 'en' | 'es' | 'fr' | 'it' | 'ja' | 'pt' | 'zh'>,
          filters.createdAt && filters.createdAt.date
            ? [filters.createdAt.date.toISOString(), new Date().toISOString()]
            : undefined // Array<string | null>,
        )
          .then(async response => {
            const medias = await this.getMediasById(response.map(m => m.media_id))
            const mediaByMediaId: { [mediaId: number]: IMediaDTO } = {}
            for (const media of medias) {
              mediaByMediaId[media.id] = media
            }
            const mediaApprovals: ISspMediaApproval[] = []
            for (const mediaApproval of response) {
              const media = mediaByMediaId[mediaApproval.media_id]
              if (!media) {
                continue
              }
              mediaApprovals.push({
                ...mediaApproval,
                approval_status_id: mediaApproval.approval_status_id as MEDIA_APPROVAL_STATUSES,
                media
              })
            }
            resolve(mediaApprovals)
          })
          .catch((error: any) => {
            console.error("Server error while getting media approvals", error)
            reject("media.approval.get.all.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while getting media approvals", ex)
        reject("media.approval.get.all.error.unexpected")
      }
    })
  },
  getMediaApprovalsCount(filters?: ISspMediaApprovalFilters): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        let comparators = undefined
        if (filters && filters.createdAt.date) {
          comparators = ["created_at,gte"]
        }
        SspMediaApprovalAPI.getApiSspMediaApprovalCount(
          undefined, // offset?: number,
          undefined, // limit?: number,
          undefined, // orderby?: Array<string>,
          comparators, // comparator?: Array<string>,
          undefined, // mediaId?: Array<number>,
          undefined, // sspId?: Array<number>,
          undefined, // sspPublisherId?: Array<number>,
          filters && filters.approvalStatuses.length > 0 ? filters.approvalStatuses : undefined, // approvalStatusId?: Array<'approved' | 'pending' | 'refused'>,
          undefined, // advertiserName?: string,
          undefined, // category?: string,
          filters && filters.languages.length > 0 ? filters.languages : undefined, // language?: Array<'de' | 'el' | 'en' | 'es' | 'fr' | 'it' | 'ja' | 'pt' | 'zh'>,
          filters && filters.createdAt && filters.createdAt.date ? [filters.createdAt.date.toISOString()] : undefined // Array<string | null>,
        )
          .then(async response => {
            resolve(response.count)
          })
          .catch((error: any) => {
            console.error("Server error while getting media approval count", error)
            reject("media.approval.get.count.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while getting media approval count", ex)
        reject("media.approval.get.count.error.unexpected")
      }
    })
  },
  getMediaVideos(mediaId: number): Promise<IMediaVideo[]> {
    return new Promise<IMediaVideo[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        MediaAPI.getApiMediaVideoList(mediaId)
          .then(response => resolve(response))
          .catch((error: any) => {
            console.error("Server error while getting media videos", error)
            reject("media.videos.get.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while getting media videos", ex)
        reject("media.videos.get.error.unexpected")
      }
    })
  },
  updateMediaApproval(mediaApproval: ISspMediaApproval, status: MEDIA_APPROVAL_STATUSES): Promise<ISspMediaApproval> {
    return new Promise<ISspMediaApproval>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        SspMediaApprovalAPI.putApiSspMediaApproval({
          id: mediaApproval.id,
          approval_status_id: status
        })
          .then(response =>
            resolve({
              ...response,
              approval_status_id: response.approval_status_id as MEDIA_APPROVAL_STATUSES,
              media: mediaApproval.media
            })
          )
          .catch((error: any) => {
            console.error("Server error while updating media approval", error)
            reject("media.approval.update.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while updating media approval", ex)
        reject("media.approval.update.error.unexpected")
      }
    })
  },
  getMediaPreviewUrl(mediaId: number): string {
    return `${getBaseUrl()}/api/media/${mediaId}/download/preview?adstanding_session_id=${getToken()}`
  }
}

function getMediaFromCampaignMedia(campaignMedia: ICampaignMedia): IMedia {
  const media = campaignMedia.media
  if (!media) {
    throw Error("Missing media")
  }
  return {
    ...(media as ICampaignMediaDTO),
    creatives: media.media_creative as ICampaignMediaCreative[],
    videos: media.media_video as ICampaignMediaVideo[],
    campaignMediaId: campaignMedia.id,
    approval_status_id: (campaignMedia.approval_status_id as APPROVAL_STATUSES) || APPROVAL_STATUSES.PENDING,
    refusal_note: campaignMedia.refusal_notes
  }
}

const getMediaApprovalDatas = (mediaApprovalsByMedias: ISspMediaApprovalsByMedia[]): IMediaApprovalData[] => {
  return mediaApprovalsByMedias.map(m => getMediaApprovalData(m))
}

export const getMediaApprovalData = (mediaApprovalsByMedia: ISspMediaApprovalsByMedia): IMediaApprovalData => {
  const media = mediaApprovalsByMedia.media
  const approvals = mediaApprovalsByMedia.ssp_approval
  const video = media.media_video && media.media_video.length > 0 ? media.media_video[0] : null
  const type = getMediaType(media)
  const categories = approvals.map(a => a.category)
  const languages = approvals.map(a => a.language)
  const statuses = approvals.map(a => a.approval_status_id)
  return {
    media_id: media.id,
    name: media.name || "-",
    status: statuses
      .filter((s, index) => statuses.indexOf(s) === index)
      .sort((s1, s2) => (s1 < s2 ? -1 : s1 === s2 ? 0 : 1))
      .join(", "),
    mime_type: type || "",
    filesize: media.filesize || 0,
    bitrate: video ? video.bitrate || 0 : 0,
    width: video ? video.width : media.width || 0,
    height: video ? video.height : media.height || 0,
    type: getMediaType(media),
    aspect_ratio: video
      ? video.height === 0
        ? 0
        : video.width / video.height
      : !media.height
        ? 0
        : (media.width || 0) / media.height,
    advertiser_name: approvals.map(a => a.advertiser_name).join(", "),
    category: categories.filter((a, index) => categories.indexOf(a) === index).join(", "),
    language: languages.filter((l, index) => languages.indexOf(l) === index).join(", "),
    created_at: media.created_at || "",
    mediaApprovalsByMedia
  }
}
